import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Gift, 
  Heart, 
  Share2, 
  Users, 
  MapPin, 
  Camera, 
  Calendar, 
  DollarSign, 
  Sparkles, 
  Star, 
  ArrowRight, 
  CheckCircle, 
  ClipboardList, 
  LayoutDashboard,
  QrCode,
  Download,
  Phone,
  MessageSquare,
  Facebook,
  Zap,
  Shield,
  Clock,
  Globe
} from 'lucide-react';
import QRCode from 'qrcode';

export default function LandingPage() {
  const sprinklesContainerRef = useRef<HTMLDivElement>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  useEffect(() => {
    const cleanup = createSprinkles();
    return cleanup;
  }, []);

  const createSprinkles = () => {
    const colors = ['#ec5a5a', '#0ea5e9', '#22c55e', '#eab308', '#a855f7', '#f43f5e', '#06b6d4', '#84cc16', '#f97316', '#ef4444', '#8b5cf6', '#06b6d4'];
    
    const createSprinkle = () => {
      if (!sprinklesContainerRef.current) return;
      
      const sprinkle = document.createElement('div');
      sprinkle.className = 'confetti-piece';
      
      // Rectangular confetti with varied sizes
      const width = Math.random() * 4 + 2; // 2-6px width
      const height = Math.random() * 6 + 3; // 3-9px height
      sprinkle.style.width = `${width}px`;
      sprinkle.style.height = `${height}px`;
      
      // Random color
      const randomColor = colors[Math.floor(Math.random() * colors.length)];
      sprinkle.style.backgroundColor = randomColor;
      
      // Random horizontal position
      sprinkle.style.left = `${Math.random() * 100}%`;
      
      // Start at a random point in the fall so no piece waits at the top.
      const duration = 4 + Math.random() * 4;
      sprinkle.style.animationDelay = `${-Math.random() * duration}s`;
      sprinkle.style.animationDuration = `${duration}s`;
      
      // Add slight border radius for more realistic look
      sprinkle.style.borderRadius = '1px';
      
      sprinklesContainerRef.current.appendChild(sprinkle);
      
      // Remove after animation completes
      setTimeout(() => {
        if (sprinkle.parentNode) {
          sprinkle.remove();
        }
      }, 8000);
    };

    // Create initial sprinkles
    for (let i = 0; i < 20; i++) {
      setTimeout(createSprinkle, i * 200);
    }

    // Continue creating sprinkles
    const interval = setInterval(createSprinkle, 300);
    
    return () => clearInterval(interval);
  };

  const generateQRCode = async () => {
    try {
      const url = await QRCode.toDataURL(window.location.origin, {
        width: 300,
        margin: 2,
        color: {
          dark: '#6b21a8',
          light: '#ffffff'
        }
      });
      setQrCodeUrl(url);
    } catch (err) {
      console.error('Error generating QR code:', err);
    }
  };

  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.download = 'giftyfy-qr-code.png';
    link.href = qrCodeUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const services = [
    {
      icon: Gift,
      title: 'Intelligent Registry Creation',
      description: 'Create stunning gift registries with AI-powered recommendations and elegant customization options',
      image: 'https://images.pexels.com/photos/264787/pexels-photo-264787.jpeg?auto=compress&cs=tinysrgb&w=800',
      color: 'from-pastel-pink-300 to-pastel-pink-400',
      animationDelay: '0s'
    },
    {
      icon: Share2,
      title: 'Seamless Sharing Experience',
      description: 'Share your registry effortlessly with QR codes, social media integration, and personalized invitations',
      image: 'https://images.pexels.com/photos/1181467/pexels-photo-1181467.jpeg?auto=compress&cs=tinysrgb&w=800',
      color: 'from-pastel-blue-300 to-pastel-blue-400',
      animationDelay: '0.1s'
    },
    {
      icon: Users,
      title: 'Collaborative Group Gifting',
      description: 'Enable friends and family to contribute together for meaningful gifts with real-time progress tracking',
      image: 'https://images.pexels.com/photos/1190297/pexels-photo-1190297.jpeg?auto=compress&cs=tinysrgb&w=800',
      color: 'from-pastel-green-300 to-pastel-green-400',
      animationDelay: '0.2s'
    },
    {
      icon: Heart,
      title: 'Gratitude Management',
      description: 'Elegant thank you note system with automated tracking and personalized messaging',
      image: 'https://images.pexels.com/photos/1729931/pexels-photo-1729931.jpeg?auto=compress&cs=tinysrgb&w=800',
      color: 'from-pastel-pink-300 to-pastel-purple-300',
      animationDelay: '0.3s'
    },
    {
      icon: ClipboardList,
      title: 'Effortless Guest Management',
      description: 'Make RSVPs effortless—for you and your guests. One platform. Zero stress.',
      image: 'https://images.pexels.com/photos/1181396/pexels-photo-1181396.jpeg?auto=compress&cs=tinysrgb&w=800',
      color: 'from-pastel-blue-300 to-pastel-purple-300',
      animationDelay: '0.4s'
    },
    {
      icon: MapPin,
      title: 'Venue Discovery',
      description: 'Find and book stunning venues that match your vision and budget with smart recommendations',
      image: 'https://images.pexels.com/photos/1444442/pexels-photo-1444442.jpeg?auto=compress&cs=tinysrgb&w=800',
      color: 'from-pastel-green-300 to-pastel-blue-300',
      animationDelay: '0.5s'
    },
    {
      icon: Camera,
      title: 'Vendor & Service Directory',
      description: 'Browse and connect with a curated list of trusted wedding and event professionals',
      image: 'https://images.pexels.com/photos/1024960/pexels-photo-1024960.jpeg?auto=compress&cs=tinysrgb&w=800',
      color: 'from-pastel-yellow-300 to-pastel-green-300',
      animationDelay: '0.6s'
    },
    {
      icon: DollarSign,
      title: 'Smart Budget Management',
      description: 'Track expenses with AI-powered insights and stay on budget with intelligent recommendations',
      image: 'https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg?auto=compress&cs=tinysrgb&w=800',
      color: 'from-pastel-purple-300 to-pastel-pink-300',
      animationDelay: '0.7s'
    },
    {
      icon: Calendar,
      title: 'Day-Of Planning & Timeline',
      description: 'Create detailed timelines and manage all day-of logistics for a stress-free event',
      image: 'https://images.pexels.com/photos/1024993/pexels-photo-1024993.jpeg?auto=compress&cs=tinysrgb&w=800',
      color: 'from-pastel-blue-300 to-pastel-green-300',
      animationDelay: '0.8s'
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Lightning Fast Setup',
      description: 'Create your registry in under 5 minutes with our intuitive interface'
    },
    {
      icon: Shield,
      title: 'Secure & Reliable',
      description: 'Bank-level security ensures your data and payments are always protected'
    },
    {
      icon: Clock,
      title: '24/7 Support',
      description: 'Our dedicated team is here to help you every step of the way'
    },
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Share your registry worldwide with multi-currency support'
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Newlywed",
      image: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=400",
      quote: "Giftyfy transformed our wedding planning experience. The elegant interface and seamless gift management made everything effortless.",
      rating: 5
    },
    {
      name: "Michael Chen",
      role: "Birthday Celebrant",
      image: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=400",
      quote: "The group gifting feature is revolutionary. My friends collaborated seamlessly to give me something truly special.",
      rating: 5
    },
    {
      name: "Emily Rodriguez",
      role: "Baby Shower Host",
      image: "https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&w=400",
      quote: "The most sophisticated registry platform I've used. Every detail is thoughtfully designed and beautifully executed.",
      rating: 5
    }
  ];

  const stats = [
    { number: '10,000+', label: 'Happy Couples' },
    { number: '50,000+', label: 'Gifts Exchanged' },
    { number: '99.9%', label: 'Uptime' },
    { number: '4.9/5', label: 'User Rating' }
  ];

  return (
    <div className="min-h-screen bg-pastel-gradient relative overflow-hidden">
      {/* Background Animations */}
      <div ref={sprinklesContainerRef} className="sparkles-container" />
      
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-confetti-pattern opacity-10"></div>
      
      <nav className="relative z-20 border-b border-white/20 bg-white/10 backdrop-blur-xl">
        <div className="container-pastel">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img src="/blue-no-logo.png" alt="Giftyfy" className="h-20" style={{ mixBlendMode: 'multiply' }} />
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="px-4 py-2 sm:px-6 sm:py-3 text-gray-700 hover:text-gray-900 font-medium transition-colors duration-300 font-poppins"
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                className="px-4 py-2 sm:px-8 sm:py-4 bg-gradient-to-r from-pastel-purple-400 to-pastel-blue-400 text-white font-semibold rounded-2xl shadow-soft-lg hover:shadow-glass-neumorphism transform hover:scale-105 transition-all duration-300 ease-out backdrop-blur-sm font-poppins"
              >
                Get Started Free
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-12 lg:py-20 overflow-hidden">
        <div className="container-pastel">
          <div className="max-w-6xl mx-auto text-center">
            <div className="mb-8 animate-fade-in-up">
              <span className="inline-flex items-center px-6 py-3 bg-white/60 border border-white/40 rounded-full text-pastel-purple-700 text-sm font-medium backdrop-blur-sm shadow-soft-lg">
                <Sparkles className="w-4 h-4 mr-2" />
                Premium Event & Registry Platform
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-poppins font-bold text-gray-800 mb-4 animate-scale-in leading-loose">
              Worried about receiving
              <span className="block text-gradient">something you'll never use?</span>
              <span className="block text-gray-700 text-3xl md:text-4xl lg:text-5xl mt-4">
                Stressing about getting your RSVPs on time?
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-primary-900 mb-6 max-w-4xl mx-auto leading-relaxed animate-fade-in-up font-poppins font-medium">
              One Registry. Endless Possibilities.
            </p>
            <p className="text-lg md:text-xl text-gray-600 mb-6 max-w-3xl mx-auto leading-relaxed animate-fade-in-up font-poppins">
              Transform your special moments into unforgettable experiences with our comprehensive event planning platform.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-6 animate-scale-in">
              <Link
                to="/signup"
                className="btn-pastel group flex items-center justify-center text-xl font-poppins px-10 py-4"
              >
                <ArrowRight className="w-6 h-6 mr-3 group-hover:translate-x-1 transition-transform duration-300" />
                <span>Register for Free Today</span>
              </Link>
            </div>
            
            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center items-center gap-3 mt-6 pt-6 border-t border-white/20">
              <div className="flex items-center space-x-2 text-gray-600">
                <CheckCircle className="w-5 h-5 text-pastel-green-500" />
                <span className="font-poppins text-xs">Free to Start</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <CheckCircle className="w-5 h-5 text-pastel-blue-500" />
                <span className="font-poppins text-xs">Secure Payments</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <CheckCircle className="w-5 h-5 text-pastel-purple-500" />
                <span className="font-poppins text-xs">24/7 Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-white/40 backdrop-blur-sm border-y border-white/30">
        <div className="container-pastel">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-poppins font-bold text-gray-800 mb-6 animate-fade-in-up">
              Your Dream Event,
              <span className="block text-gradient">Simplified</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-poppins animate-fade-in-up">
              From intimate gatherings to grand celebrations, our platform provides all the tools you need to create memorable experiences.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="group card-pastel p-8 hover:scale-105 transition-all duration-500 animate-fade-in-up overflow-hidden"
                style={{ animationDelay: service.animationDelay }}
              >
                <div className="relative mb-6">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-48 object-cover rounded-2xl shadow-soft-lg"
                  />
                </div>
                <h3 className="text-xl font-poppins font-semibold text-gray-800 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 font-poppins leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding">
        <div className="container-pastel">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-poppins font-bold text-gray-800 mb-6">
              Why Choose
              <span className="block text-gradient">Giftyfy?</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="text-center group animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-20 h-20 bg-gradient-to-r from-pastel-purple-300 to-pastel-blue-300 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-glass-neumorphism">
                  <benefit.icon className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-poppins font-semibold text-gray-800 mb-4">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 font-poppins leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QR Code Section */}
      <section className="section-padding bg-white/40 backdrop-blur-sm border-y border-white/30">
        <div className="container-pastel">
          <div className="max-w-4xl mx-auto text-center">
            <div className="card-pastel p-12 md:p-16">
              <div className="mb-8">
                <QrCode className="w-20 h-20 mx-auto mb-6 text-pastel-purple-600" />
              </div>
              <h2 className="text-4xl md:text-5xl font-poppins font-bold text-gray-800 mb-6">
                Access Giftyfy
                <span className="block text-gradient">Anytime, Anywhere</span>
              </h2>
              <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto font-poppins leading-relaxed">
                Scan this QR code to access Giftyfy instantly on any device. Perfect for sharing with friends and family!
              </p>
              
              {qrCodeUrl && (
                <div className="flex flex-col items-center space-y-6">
                  <div className="bg-white p-6 rounded-3xl shadow-glass-neumorphism">
                    <img src={qrCodeUrl} alt="Giftyfy QR Code" className="w-48 h-48" />
                  </div>
                  <button
                    onClick={downloadQRCode}
                    className="btn-pastel group flex items-center justify-center font-poppins"
                  >
                    <Download className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
                    <span>Download QR Code</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-white/40 backdrop-blur-sm border-y border-white/30">
        <div className="container-pastel">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-poppins font-bold text-gray-800 mb-6 leading-normal">
              What Our Users
              <span className="block text-gradient py-1">Are Saying</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="card-pastel p-8 text-center animate-fade-in-up"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-20 h-20 rounded-full mx-auto mb-6 object-cover shadow-soft-lg"
                />
                <div className="flex justify-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-pastel-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 font-poppins italic leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-semibold text-gray-800 font-poppins">{testimonial.name}</p>
                  <p className="text-gray-600 text-sm font-poppins">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container-pastel">
          <div className="max-w-4xl mx-auto text-center">
            <div className="card-pastel p-12 md:p-16 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-pastel-purple-200/30 to-pastel-blue-200/30 animate-gradient-shift"></div>
              <div className="relative z-10">
                <div className="mb-8">
                  <Gift className="w-20 h-20 mx-auto mb-6 text-pastel-blue-600 animate-float-subtle" />
                </div>
                <h2 className="text-4xl md:text-5xl font-poppins font-bold text-gray-800 mb-6">
                  Ready to Create Something
                  <span className="block text-gradient">Extraordinary?</span>
                </h2>
                <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto font-poppins leading-relaxed">
                  Join thousands of couples who have made their celebrations unforgettable with our premium platform. Start your journey today.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-6">
                  <Link
                    to="/signup"
                    className="btn-pastel group flex items-center justify-center text-xl font-poppins"
                  >
                    <ArrowRight className="w-6 h-6 mr-3 group-hover:translate-x-1 transition-transform duration-300" />
                    <span>Start Your Free Registry</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-16 bg-white/60 backdrop-blur-xl border-t border-white/30">
        <div className="container-pastel">
          <div className="flex flex-col items-center space-y-8">
            <div className="flex items-center space-x-3">
              <img src="/blue-no-logo.png" alt="Giftyfy" className="h-20" style={{ mixBlendMode: 'multiply' }} />
            </div>
            <p className="text-gray-600 text-center max-w-md font-poppins">
              Making gift-giving delightful and memorable with elegance and sophistication.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <a 
                href="tel:+94779257975" 
                className="inline-flex items-center px-6 py-3 rounded-2xl glass-effect hover:bg-white/30 transition-all duration-300 text-gray-700 hover:text-gray-900 cursor-pointer font-poppins shadow-soft-lg"
              >
                <Phone className="w-4 h-4 mr-2" />
                <span>+94 779257975</span>
              </a>
              <a 
                href="https://wa.me/94779257975" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-flex items-center px-6 py-3 rounded-2xl glass-effect hover:bg-white/30 transition-all duration-300 text-gray-700 hover:text-gray-900 cursor-pointer font-poppins shadow-soft-lg"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                <span>WhatsApp</span>
              </a>
              <a 
                href="https://web.facebook.com/GiftyfyOfficial" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-flex items-center px-6 py-3 rounded-2xl glass-effect hover:bg-white/30 transition-all duration-300 text-gray-700 hover:text-gray-900 cursor-pointer font-poppins shadow-soft-lg"
              >
                <Facebook className="w-4 h-4 mr-2" />
                <span>Facebook</span>
              </a>
            </div>
            <div className="flex items-center space-x-6 text-sm text-gray-500 font-poppins">
              <Link to="/privacy-policy" className="hover:text-gray-700 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/refund-policy" className="hover:text-gray-700 transition-colors">
                Refund Policy
              </Link>
              <Link to="/terms-conditions" className="hover:text-gray-700 transition-colors">
                Terms & Conditions
              </Link>
            </div>
            <p className="text-sm text-gray-500 font-poppins">
              © {new Date().getFullYear()} Giftyfy. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}